import {
  FeatherLock,
  FeatherMail,
  FeatherPhone,
  FeatherShield,
} from "@subframe/core";
import { IconWithBackground } from "../ui/components/IconWithBackground";
import image1 from "../assets/fastrupeeslogo.png";
import SecurityNotice from "./SecurityNotice";
import { useTheme } from "../context/ThemeContext";
import { trackEvent } from "../services/firebase";

const Footer = () => {
  const primaryColor = import.meta.env.VITE_PRIMARY_COLOR || '#5B1A8B';
  const secondaryColor = import.meta.env.VITE_SECONDARY_COLOR || '#6CC24A';
  const { isDark } = useTheme();

  const mutedText = isDark ? 'text-gray-400' : 'text-gray-600';
  const strongText = isDark ? 'text-white' : 'text-gray-900';
  const linkClass = "text-sm cursor-pointer transition-all duration-300 hover:translate-x-1";

  const columnLabel = (label: string) => (
    <div className="flex items-center gap-2 mb-1">
      <span className="w-1.5 h-4 rounded-full" style={{ backgroundColor: primaryColor }} />
      <span className="text-sm font-bold uppercase tracking-wide" style={{ color: primaryColor }}>
        {label}
      </span>
    </div>
  );

  const quickLinks = [
    { href: "/personal-loan-salaried-professionals", label: "Personal Loan for Salaried" },
    { href: "/quick-personal-loan", label: "Quick Personal Loan" },
    { href: "/online-personal-loan", label: "Online Personal Loan" },
    { href: "/personal-loan-up-to-50000", label: "Personal Loan up to ₹50,000" },
    { href: "/personal-loan-eligibility", label: "Personal Loan Eligibility" },
    { href: "/documents-required-for-personal-loan", label: "Documents Required" },
    { href: "/about", label: "About Us" },
  ];

  const shortTermLinks = [
    { href: "/short-term-loan", label: "Short-Term Loan" },
    { href: "/short-term-loan/mumbai", label: "Short-Term Loan in Mumbai" },
    { href: "/short-term-loan/delhi", label: "Short-Term Loan in Delhi" },
    { href: "/short-term-loan/bengaluru", label: "Short-Term Loan in Bengaluru" },
    { href: "/short-term-loan/pune", label: "Short-Term Loan in Pune" },
    { href: "/short-term-loan/ahmedabad", label: "Short-Term Loan in Ahmedabad" },
    { href: "/short-term-loan/hyderabad", label: "Short-Term Loan in Hyderabad" },
  ];

  const legalLinks = [
    { href: "/terms-condition", label: "Terms & Conditions" },
    { href: "/privacy-policy", label: "Privacy Policy" },
    { href: "/fair-practice-code", label: "Fair Practice Code" },
  ];

  return (
    <div>
      <SecurityNotice />
      <footer
        className="relative w-full border-t overflow-hidden pt-14 pb-8"
        style={{
          background: isDark ? '#0A0A0A' : '#F8F8F8',
          borderColor: `${primaryColor}20`,
        }}
      >

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

          {/* ── Logo + 3 columns side by side ── */}
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">

            {/* Logo + tagline */}
            <div className="flex flex-col items-center sm:items-start gap-3 lg:w-64 shrink-0">
              <img className="w-48 sm:w-56 object-contain" src={image1} alt={import.meta.env.VITE_COMPANY_NAME} loading="lazy" />
              <p className={`text-sm leading-relaxed max-w-xs text-center sm:text-left ${mutedText}`}>
                Fast, secure, and fully digital instant loans — built around your trust.
              </p>
              <div className="flex flex-col gap-2.5">
                <a
                  href={`tel:${import.meta.env.VITE_COMPANY_PHONE}`}
                  onClick={() => trackEvent("phone_call_click", { location: "footer" })}
                  className="flex items-center gap-2 hover:scale-105 transition-transform duration-300"
                >
                  <IconWithBackground size="small" icon={<FeatherPhone style={{ color: primaryColor }} />} />
                  <span className="text-sm" style={{ color: primaryColor }}>{import.meta.env.VITE_COMPANY_PHONE}</span>
                </a>
                <a href={`mailto:${import.meta.env.VITE_COMPANY_EMAIL}`} className="flex items-center gap-2 hover:scale-105 transition-transform duration-300">
                  <IconWithBackground size="small" icon={<FeatherMail style={{ color: primaryColor }} />} />
                  <span className="text-sm" style={{ color: primaryColor }}>{import.meta.env.VITE_COMPANY_EMAIL}</span>
                </a>
              </div>
            </div>

          {/* ── Three columns: Compliance | Legal | Quick Links ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-10 flex-1">

            {/* Compliance */}
            <div className="flex flex-col items-center sm:items-start gap-3">
              {columnLabel("Compliance")}
              <div className="w-full flex flex-col items-center sm:items-start gap-3 text-center sm:text-left">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: `${primaryColor}10` }}>
                  <FeatherShield className="flex-shrink-0" style={{ color: secondaryColor }} />
                  <span className={`text-xs font-semibold ${strongText}`}>
                    RBI License: {import.meta.env.VITE_RBI_LICENSE}
                  </span>
                </div>
                <div>
                  <p className={`text-xs ${mutedText}`}>Grievance Officer</p>
                  <p className={`text-sm font-semibold ${strongText}`}>{import.meta.env.VITE_BRAND_GRIEVANCE_OFFICER_EMAIL}</p>
                  <a href={`tel:${import.meta.env.VITE_BRAND_GRIEVANCE_OFFICER_PHONE}`}
                    className="text-sm font-semibold hover:underline" style={{ color: primaryColor }}>
                    {import.meta.env.VITE_BRAND_GRIEVANCE_OFFICER_PHONE}
                  </a>
                </div>
                <div>
                  <p className={`text-xs ${mutedText}`}>Nodal Officer</p>
                  <p className={`text-sm font-semibold ${strongText}`}>{import.meta.env.VITE_BRAND_NODAL_OFFICER_EMAIL}</p>
                  <a href={`tel:${import.meta.env.VITE_BRAND_NODAL_OFFICER_PHONE}`}
                    className="text-sm font-semibold hover:underline" style={{ color: primaryColor }}>
                    {import.meta.env.VITE_BRAND_NODAL_OFFICER_PHONE}
                  </a>
                </div>
              </div>
            </div>

            {/* Legal */}
            <div className="flex flex-col items-center sm:items-start gap-3">
              {columnLabel("Legal")}
              <div className="flex flex-col items-center sm:items-start gap-2">
                {legalLinks.map(link => (
                  <a key={link.label} href={link.href} className={linkClass}
                    style={{ color: isDark ? '#D1D5DB' : '#374151' }}
                    onMouseEnter={e => (e.currentTarget.style.color = secondaryColor)}
                    onMouseLeave={e => (e.currentTarget.style.color = isDark ? '#D1D5DB' : '#374151')}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="flex flex-col items-center sm:items-start gap-3">
              {columnLabel("Quick Links")}
              <div className="flex flex-col items-center sm:items-start gap-2">
                {quickLinks.map(link => (
                  <a key={link.label} href={link.href} className={linkClass}
                    style={{ color: isDark ? '#D1D5DB' : '#374151' }}
                    onMouseEnter={e => (e.currentTarget.style.color = secondaryColor)}
                    onMouseLeave={e => (e.currentTarget.style.color = isDark ? '#D1D5DB' : '#374151')}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>


            {/* Short-Term Loans */}
            <div className="flex flex-col items-center sm:items-start gap-3">
              {columnLabel("Short-Term Loans")}
              <div className="flex flex-col items-center sm:items-start gap-2">
                {shortTermLinks.map(link => (
                  <a key={link.label} href={link.href} className={linkClass}
                    style={{ color: isDark ? '#D1D5DB' : '#374151' }}
                    onMouseEnter={e => (e.currentTarget.style.color = secondaryColor)}
                    onMouseLeave={e => (e.currentTarget.style.color = isDark ? '#D1D5DB' : '#374151')}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
          </div>

          {/* ── Trust strip + bottom bar — single row on desktop ── */}
          <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-6 gap-y-3 mt-10 pt-6 border-t text-center" style={{ borderColor: `${primaryColor}25` }}>
            <div className="flex items-center gap-2 rounded-full border px-3 py-1.5" style={{ borderColor: `${primaryColor}40` }}>
              <IconWithBackground variant="success" size="small" icon={<FeatherLock className="p-1 bg-green-500/90 rounded-full" />} />
              <span className={`text-base font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>256-bit SSL Secured</span>
            </div>

            <span className="hidden sm:inline opacity-30" style={{ color: primaryColor }}>|</span>
            <span className={`text-base font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Made in India 🇮🇳</span>

            <span className="hidden sm:inline opacity-30" style={{ color: primaryColor }}>|</span>
            <span className="text-sm" style={{ color: primaryColor }}>
              © {new Date().getFullYear()} {import.meta.env.VITE_COMPANY_NAME}. All rights reserved.
            </span>

            <span className="hidden sm:inline opacity-30" style={{ color: primaryColor }}>|</span>
            <span className="text-sm" style={{ color: secondaryColor }}>
              NBFC Partner: {import.meta.env.VITE_COMPANY_NBFC}
            </span>

            <span className="hidden sm:inline opacity-30" style={{ color: primaryColor }}>|</span>
            <span className="text-sm" style={{ color: secondaryColor }}>
              Powered By: {import.meta.env.VITE_POWERED_BY}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Footer;
